import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { ClientSessionService, UiSession } from '@brighthub/uisession';
import { ClientSearchResultItem } from '../client-search-api.model';
import {
  OmniSearchService,
  type OmniSearchResultComponent,
} from '@brighthub/search';
import { FormatPrototypePhonePipe } from './format-prototype-phone.pipe';

/**
 * One row in the hit `<dl>`. `kind` drives template formatting: `date` uses {@link DatePipe},
 * `phone` uses {@link FormatPrototypePhonePipe}, `text` is interpolated as-is.
 */
type DisplayFieldRow =
  | { label: string; kind: 'text'; value: string | null }
  | { label: string; kind: 'date'; value: Date | null }
  | { label: string; kind: 'phone'; value: string | null };

/** Icon, label, tooltip, and BEM suffix for {@link ClientPrototypeSearchResultComponent.chatActionHint}. */
type ChatActionHint = {
  iconClass: string;
  label: string;
  styleModifier: 'existing' | 'new';
  tooltip: string;
};

/**
 * Renders a single prototype client-search hit for omni-search and the autocomplete widget: client name,
 * an action hint (icon + label) in the heading row’s top-right for **Open chat** vs **New chat**, and a compact
 * field list (DOB, company, address, employee ID, preferred phone).
 * The whole card is the control: clicking opens or resumes the client conversation (same behavior as the former
 * popover CTA). Implements {@link OmniSearchResultComponent} for {@link ClientSearchResultItem}.
 *
 * @remarks
 * Click handling uses `stopPropagation` so the omni result wrapper’s default click handler does not run.
 * Chat success clears the omni UI via {@link OmniSearchService.requestClearOmniSearchUi}.
 */
@Component({
  selector: 'bn-client-prototype-search-result',
  standalone: true,
  imports: [CommonModule, FormatPrototypePhonePipe],
  providers: [TitleCasePipe],
  templateUrl: './client-prototype-search-result.component.html',
  styleUrl: './client-prototype-search-result.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientPrototypeSearchResultComponent
  implements OmniSearchResultComponent<ClientSearchResultItem>
{
  private readonly _sessions = inject(ClientSessionService);
  private readonly _toast = inject(MessageService);
  private readonly _omniSearch = inject(OmniSearchService);
  private readonly _titleCase = inject(TitleCasePipe);

  private readonly _item = signal<ClientSearchResultItem | null>(null);

  /**
   * Person row from the search API (or a hydrated local match with {@link ClientSearchResultItem.sourceUiSession}).
   * The setter keeps a signal so computeds react; the getter is required by {@link OmniSearchResultComponent}.
   */
  @Input({ required: true })
  set data(value: ClientSearchResultItem) {
    this._item.set(value);
  }
  get data(): ClientSearchResultItem {
    return this._item()!;
  }

  /** Omni provider id; reserved for callers that distinguish providers (unused in this template). */
  @Input() providerId = '';
  /** Kept for omni-search / autocomplete compatibility; layout does not vary. */
  @Input() compact = false;

  /** Active session for this person in the “Your clients” list, if any. */
  readonly activeSessionForPerson = computed((): UiSession | null => {
    this._sessions.sessions();
    const item = this._item();
    if (!item) {
      return null;
    }
    if (item.sourceUiSession) {
      return this._sessions.findActiveSessionForSubject(
        item.sourceUiSession.subjectId
      );
    }
    const guid = item.guid;
    if (!guid) {
      return null;
    }
    return this._sessions.findActiveSessionForSubject(guid);
  });

  /** True when {@link activeSessionForPerson} found an open tab for this subject. */
  readonly hasActiveChat = computed(() => !!this.activeSessionForPerson());

  /**
   * Visual + copy for the row action when {@link canOpenChat} is true: resume an open tab vs start a new
   * conversation. `null` when the row is not selectable.
   */
  readonly chatActionHint = computed((): ChatActionHint | null => {
    if (!this.canOpenChat()) {
      return null;
    }
    if (this.hasActiveChat()) {
      return {
        iconClass: 'pi pi-comments',
        label: 'Open chat',
        styleModifier: 'existing',
        tooltip: 'Resume your open conversation with this client',
      };
    }
    return {
      iconClass: 'pi pi-plus-circle',
      label: 'New chat',
      styleModifier: 'new',
      tooltip: 'Start a new conversation with this client',
    };
  });

  /** Display name from `firstName` / `lastName`, or a fallback label when both are empty. */
  readonly fullName = computed(() => {
    const item = this._item();
    if (!item) {
      return 'Unknown client';
    }
    const parts = [item.firstName, item.lastName].filter(
      (x): x is string => !!x?.trim()
    );
    return parts.length ? parts.join(' ') : 'Unknown client';
  });

  /** Raw `customer` string from the payload (trimmed). */
  readonly companyDisplay = computed(() => {
    const item = this._item();
    if (!item) {
      return '';
    }
    return item.customer?.trim() ?? '';
  });

  /** Title case for display (company / customer label). */
  readonly companyDisplayTitleCase = computed(() => {
    const raw = this.companyDisplay().trim();
    if (!raw) {
      return '';
    }
    return this._titleCase.transform(raw) ?? raw;
  });

  /**
   * Key/value rows shown on the card: DOB, company, mailing address, employee ID, preferred phone
   * (aligned with prototype client search payload).
   */
  readonly displayFieldRows = computed((): DisplayFieldRow[] => {
    const d = this._item();
    if (!d) {
      return [];
    }
    const line = (parts: (string | null | undefined)[]) =>
      parts.filter((x) => x?.trim()).join(', ') || null;
    const companyRaw = d.customer?.trim() ?? '';
    const companyTitle = companyRaw
      ? this._titleCase.transform(companyRaw) ?? companyRaw
      : null;
    const dobParsed = this._parseDob(d.dateOfBirth);
    const dobRaw = d.dateOfBirth?.trim() || null;
    const dobRow: DisplayFieldRow = dobParsed
      ? { label: 'Date of birth', kind: 'date', value: dobParsed }
      : { label: 'Date of birth', kind: 'text', value: dobRaw };
    return [
      dobRow,
      { label: 'Company', kind: 'text', value: companyTitle },
      {
        label: 'Address',
        kind: 'text',
        value: line([d.mailingStreet, d.mailingCity, d.mailingState]),
      },
      { label: 'Employee ID', kind: 'text', value: d.employeeId },
      {
        label: 'Preferred phone',
        kind: 'phone',
        value: d.preferredPhone?.trim() || null,
      },
    ];
  });

  /**
   * Accessible name for the row button: action + client name, or a disabled reason when not selectable.
   */
  readonly rowAriaLabel = computed(() => {
    const name = this.fullName();
    if (!this.canOpenChat()) {
      return `${name}. Cannot open chat: no person identifier.`;
    }
    const action = this.activeSessionForPerson() ? 'Go to chat with' : 'Start chat with';
    return `${action} ${name}`;
  });

  /**
   * Parses API date-of-birth strings for {@link DatePipe} (`MM/dd/yyyy` in the template); invalid or empty
   * input yields `null` (template shows em dash).
   */
  private _parseDob(value: string | null | undefined): Date | null {
    if (!value?.trim()) {
      return null;
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  /**
   * Primary row interaction: opens or resumes the client conversation and clears omni search UI.
   * Stops propagation so the omni result container does not treat the click as a generic dismiss.
   *
   * @param event - Click event from the row.
   */
  onRowActivate(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.onChatAction();
  }

  /**
   * Selects or creates the client conversation from the current hit and clears omni search UI.
   * Prefers {@link activeSessionForPerson}, then {@link ClientSearchResultItem.sourceUiSession}, then `guid`.
   */
  onChatAction(): void {
    const item = this._item();
    if (!item) {
      return;
    }
    const existing = this.activeSessionForPerson();
    if (existing) {
      this._sessions.createOrSelectClientConversation(existing);
    } else if (item.sourceUiSession) {
      this._sessions.createOrSelectClientConversation(item.sourceUiSession);
    } else {
      const guid = item.guid;
      if (!guid) {
        this._toast.add({
          severity: 'warn',
          summary: 'Cannot open chat',
          detail: 'No person GUID is available for this result.',
          life: 5000,
        });
        return;
      }
      this._sessions.createOrSelectClientConversation(guid);
    }
    this._omniSearch.requestClearOmniSearchUi();
  }

  /** False when neither a GUID nor a local {@link ClientSearchResultItem.sourceUiSession} is available. */
  readonly canOpenChat = computed(() => {
    const item = this._item();
    return !!(item?.guid || item?.sourceUiSession);
  });
}
